import { Controller, useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { TextField, Select, IconButton, Button, Text } from "@aprosoja/ui";

import { calculationStepFormSchema, CalculationStepFormValues } from "./schema";
import useCalculationStepForm from "./use-calculation-step-form";
import { useSignUpContext } from "../../../context/sign-up.context";

const defaultFieldsValue = {
  area: "",
  plantedCulture: "",
  state: { value: "", label: "" },
};

const CalculationStepForm = () => {
  const {
    control,
    handleSubmit,
    getValues,
    reset,
    setValue,
    watch,
    formState: { isSubmitSuccessful },
  } = useForm<CalculationStepFormValues>({
    resolver: zodResolver(calculationStepFormSchema),
    defaultValues: {
      properties: [defaultFieldsValue],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "properties",
  });

  const { onSubmit, optionsState, statesData } = useCalculationStepForm();
  const { signUpReducer, setSignUpReducer } = useSignUpContext();

  const handleStateChange = (index: number) => {
    if (statesData) {
      const selectedCities = statesData?.data.find(
        (state) => state.id === getValues(`properties.${index}.state`).value
      );
      const stateCities = selectedCities?.cities?.map((city) => ({
        label: city.name,
        value: city.id as string,
      }));

      return stateCities;
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col py-size-x8 px-size-x6 gap-size-x8 bg-neutral-100 rounded-border-radius-large"
    >
      {isSubmitSuccessful ? (
        <>
          <div className="flex flex-col gap-size-x2">
            <Text type="paragraphL">
              R${signUpReducer?.calculationResults?.monthlyPayment}/mensais
            </Text>
            <Text type="displayXSmall" color="th-primary-color-main">
              R$ {signUpReducer?.calculationResults?.annualPayment} /anual
            </Text>
          </div>
          <div className="flex flex-col gap-size-x4">
            <Button
              onClick={() =>
                setSignUpReducer({ type: "step", stepPayload: "sign-up" })
              }
              className="rounded-border-radius-small"
            >
              Associar
            </Button>
            <Button
              variant="outlined"
              onClick={() => reset()}
              className="rounded-border-radius-small"
            >
              Recalcular
            </Button>
          </div>
        </>
      ) : (
        <>
          {fields.map((item, index) => (
            <div className="flex gap-size-x2 items-center" key={item.id}>
              <div className="flex flex-col flex-1 gap-size-x2">
                <div className="flex gap-size-x2">
                  <div className="flex w-size-x30">
                    <Controller
                      name={`properties.${index}.area`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          required
                          label="Área"
                          errorMessage={fieldState?.error?.message}
                          size="large"
                          className="bg-white hover:bg-white"
                          mask="number"
                          endUnit="Ha"
                        />
                      )}
                    />
                  </div>
                  <Controller
                    name={`properties.${index}.plantedCulture`}
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        required
                        label="Cultura plantada"
                        errorMessage={fieldState?.error?.message}
                        size="large"
                        className="bg-white hover:bg-white"
                      />
                    )}
                  />
                </div>
                <div className="flex gap-size-x2">
                  <div className="flex w-size-x30">
                    <Controller
                      name={`properties.${index}.state`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <Select
                          {...field}
                          required
                          options={optionsState.map((state) => ({
                            label: state.label,
                            value: state.value,
                          }))}
                          label="Estado"
                          errorMessage={fieldState?.error?.message}
                          onValueChange={(selectedStateId) => {
                            field.onChange(
                              optionsState.find(
                                (state) => state.value === selectedStateId
                              )
                            );
                            setValue(`properties.${index}.city`, undefined, {
                              shouldValidate: true,
                            });
                          }}
                          value={field.value.value}
                          triggerClassName="bg-white hover:bg-white"
                          size="large"
                        />
                      )}
                    />
                  </div>
                  <Controller
                    name={`properties.${index}.city`}
                    control={control}
                    render={({ field, fieldState }) => {
                      return (
                        <Select
                          {...field}
                          options={handleStateChange(index) || []}
                          label="Município"
                          errorMessage={fieldState?.error?.message}
                          onValueChange={(value) =>
                            field.onChange(
                              handleStateChange(index)?.find(
                                (city) => city.value === value
                              )
                            )
                          }
                          value={field.value?.value}
                          triggerClassName="bg-white hover:bg-white"
                          size="large"
                          disabled={!watch(`properties.${index}.state`).value}
                        />
                      );
                    }}
                  />
                </div>
              </div>
              {fields.length > 1 && (
                <IconButton
                  name="delete"
                  color=""
                  onClick={() => remove(index)}
                  className="rounded-border-radius-tiny"
                />
              )}
            </div>
          ))}
          <div className="flex w-full justify-end">
            <Button
              className="w-[240px] rounded-border-radius-small"
              variant="outlined"
              disabled={fields.length >= 3}
              onClick={() => {
                fields.length < 3 && append(defaultFieldsValue);
              }}
            >
              Adicionar mais propriedades
            </Button>
          </div>
          <Button type="submit" className="rounded-border-radius-small">
            Calcular
          </Button>
        </>
      )}
    </form>
  );
};

export default CalculationStepForm;
